import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideOpinionLead from "../components/GuideOpinionLead";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "bike-security")!;

const OPINION = {
    authorNote: "Spencer · rides a fully-loaded GS",
    heading: "How I lock up my own bike",
    intro:
        "Here's what I actually run on my own bike, and the thinking behind it. The general playbook follows below — but this is the version I trust with my own machine.",
    chips: [
        "🔒 2× Litelok X3",
        "📡 GPS tracker",
        "🏷 2× AirTags",
        "🧥 Tatty cover",
        "🅿️ Secure hotel parking",
    ],
    points: [
        {
            title: "Two D-locks, one on each wheel",
            body:
                "I run two Litelok X3s — one through the front wheel, one through the rear — plus a cable to loop the bike to something solid when I'm on tour. With both wheels locked you can't roll it away — not even by lifting one end and wheeling it off on the other. To take it they'd have to carry the whole bike, both wheels off the ground, and a fully loaded GS is heavy enough that it needs a proper team to lift, which is far more hassle than most thieves want.",
        },
        {
            title: "A tracker, backed up with AirTags",
            body:
                "The bike's fitted with a tracker that rings my phone the moment it's moved. That's the one I value most when I've stopped for a bit of sightseeing — it lets me wander off and enjoy the stop with a lot more confidence. I've also tucked two Apple AirTags on the bike as a backup; they're a little easier to home in on if it ever comes to finding it.",
        },
        {
            title: "Service stations — lock up and stay watchful",
            body:
                "At service stations I always lock the front wheel — it only takes a couple of minutes — and keep eyes on the bike wherever I can. In the past I've done a dummy walk-around to check there are no spotters scoping the bikes. A bit over the top, maybe, but service stations near built-up areas are genuinely risky. In a group we work in shifts, so there's always someone with the bikes while the others take a break.",
        },
        {
            title: "Overnight — secure hotel parking every time",
            body:
                "I always research each stop before I arrive. In a city or large town, secure hotel parking is the top-tier option — it's where I head first every time — usually around €20 a bay, and we can get four bikes into one, so split between us it's easily worth the money.",
        },
        {
            title: "Always cover it — a tatty cover is best",
            body:
                "I cover the bike wherever it sleeps. A scruffy old cover is actually the best kind: it keeps the weather off, but more importantly it doesn't advertise what's underneath — and I do it even inside a secure parking bay.",
        },
    ],
};

type Section = {
    heading: string;
    intro?: string;
    items?: { title: string; body: string }[];
    countries?: { flag: string; name: string; body: string }[];
    photoPlaceholder?: string;
};

const SECTIONS: Section[] = [
    {
        heading: "How bikes actually get stolen",
        intro:
            "Knowing the playbook tells you what to defend against. Most bike theft is one of three patterns — opportunistic from a driveway, targeted from a public spot after being scouted, or lifted into a van in under two minutes by an organised crew.",
        items: [
            {
                title: "From home",
                body:
                    "Around half of UK bike thefts happen at the owner's address. Bikes on driveways, behind wooden side-gates, or in unlit garages get walked off in the small hours. Thieves often scout daytime and return at 2–4am with a van and a battery-powered angle grinder.",
            },
            {
                title: "From public parking",
                body:
                    "A handful of bays at busy stations, shopping centres and tourist car parks are hit repeatedly. Locks get cut, the bike is rolled to a waiting van or out of frame of the nearest camera. Often a two-person job — one cuts, one watches.",
            },
            {
                title: "Battery angle grinders changed the game",
                body:
                    "Until the late 2010s, cutting through a 16mm hardened chain in the street was a 15-minute job. With modern brushless cordless grinders it's 30–60 seconds. Anyone relying on a single chain is relying on luck.",
            },
        ],
    },
    {
        heading: "At home — the most important place to get right",
        intro:
            "Most bikes are stolen from where their owner sleeps. A few hours of work and a couple of hundred pounds in kit makes you a wildly less attractive target.",
        items: [
            {
                title: "Don't leave keys near the door",
                body:
                    "Modern keyless bikes can be hot-wired in seconds via a relay attack — one thief amplifies the signal from inside your house, the other unlocks and rides off. Store keys in a Faraday pouch or a metal tin, well away from external walls.",
            },
            {
                title: "Ground anchor",
                body:
                    "A hardened steel anchor bolted into a concrete floor (or resin-bonded into brick) is non-negotiable. Look for a Sold Secure rated ground anchor. Mount it in the garage floor or driveway so the chain runs through the bike frame, not just a wheel.",
            },
            {
                title: "Chain + disc lock, not chain alone",
                body:
                    "A chain anchors the bike. A disc lock makes the bike unrollable, so a thief can't push it to a quieter spot to cut the chain. Use both. A Sold Secure Diamond rated 16mm+ chain through the frame, an alarmed disc lock on the front rotor.",
            },
            {
                title: "Cover the bike",
                body:
                    "Out of sight is most of the battle. A plain dark cover that hides the brand and model means nobody driving past at night sees an expensive sportbike. Cheap, fast, surprisingly effective.",
            },
            {
                title: "Lighting and movement detection",
                body:
                    "PIR-triggered floodlights on the driveway. A motion-activated camera at the garage door. Alone they don't stop a determined thief but they push the lazy ones onto the next target.",
            },
            {
                title: "Garage door — get a proper one",
                body:
                    "Wooden up-and-overs are kicked in. Cheap roller doors are jemmied open in seconds. A steel up-and-over with internal deadlocks, or a steel side-hinged door with two locks, is the realistic minimum if the bike lives in the garage.",
            },
        ],
    },
    {
        heading: "On the road — locking up where you're staying",
        intro:
            "On tour you're stopping at hotels, restaurants, fuel stations. Each is a chance for someone to walk off with the bike. The defensive kit gets smaller; the principles stay the same.",
        items: [
            {
                title: "Pre-book bike-friendly hotels",
                body:
                    "Many continental hotels list 'parking moto' or 'garage' in their facilities. Booking.com lets you filter for 'secure parking' — use it. Confirm by email before booking. The fee is usually €5–€15 and worth every cent.",
            },
            {
                title: "If there's no hotel garage",
                body:
                    "Park where the front-desk staff can see the bike from their counter. Failing that, under a street light, in view of a café window, or next to other bikes (you don't have to be faster than the bear). Cover it. Lock to something immovable if possible.",
            },
            {
                title: "Carry a real chain, not just a cable",
                body:
                    "Cable locks are theft deterrents only against amateurs. A 1m hardened chain with 16mm links weighs ~4kg but goes through frame + railings. Pannier-able if you accept the weight penalty.",
            },
            {
                title: "Alarmed disc lock for café stops",
                body:
                    "An alarmed disc lock — clip it onto the front disc when you stop for lunch. A 105dB siren when it senses movement is enough to scare off anyone who fancies a push. £30–£80, fits in a tank bag.",
            },
        ],
    },
    {
        heading: "European theft hotspots and country quirks",
        intro:
            "Bike-theft rates vary wildly across Europe, and so does how the local police handle a foreign rider reporting one. A bit of homework before you go saves a lot of grief at the roadside.",
        countries: [
            {
                flag: "🇮🇹",
                name: "Italy",
                body:
                    "The single highest-risk country for organised motorbike theft in Western Europe. Rome, Naples and Milan are the hotspots — bikes lifted into vans in minutes. Bologna and Florence aren't far behind. Never leave a bike on the street overnight; book a hotel garage or risk paying the excess on your insurance. Some insurers exclude theft cover south of Rome entirely — check the small print before you cross the Apennines. Always carry the original V5C; Carabinieri stops are common and copies aren't always accepted.",
            },
            {
                flag: "🇫🇷",
                name: "France",
                body:
                    "Lower rate than Italy, but Paris and Marseille have an active street-theft scene. The ferry queues at Calais are a known hunting ground for opportunists — keep gear on the bike and an eye on it during boarding. Pétanque-pitch tactics at the péages: thieves spot bikes with expensive luggage at toll plazas and follow them to the next service area. Don't leave panniers on the bike unattended at autoroute aires.",
            },
            {
                flag: "🇪🇸",
                name: "Spain",
                body:
                    "Barcelona has the worst reputation by some distance — Las Ramblas, the Gothic Quarter, and the Sant Antoni area in particular. Madrid and the Costa del Sol resorts (Marbella, Estepona) also see organised crews. The Guardia Civil are responsive but expect a long wait at the comisaría to file a denuncia, which your insurer will need. Spanish insurance excess for foreign bikes can be steep — read your UK policy's territorial extension carefully.",
            },
            {
                flag: "🇩🇪",
                name: "Germany",
                body:
                    "Generally a low theft rate by European standards — police presence is high and recovery rates are above the European average. Berlin and Hamburg are the exceptions in cities. Autobahn service areas (Raststätten) are safer than in France but still don't leave the bike untouched overnight. The Polizei produces reports in German only — keep a translation app handy for the insurance claim.",
            },
            {
                flag: "🇨🇭",
                name: "Switzerland",
                body:
                    "Lowest theft rate in Europe and culturally trusting parking. Geneva and Zurich are the exceptions; the rest of the country is genuinely safe to park overnight on the street. Don't drop your guard entirely — your insurance still requires the same lock as in the UK, and the excess for Swiss theft is the same regardless of how rare it is.",
            },
            {
                flag: "🇳🇱🇧🇪",
                name: "Netherlands & Belgium",
                body:
                    "Bicycle theft is endemic to the point of cultural acceptance; motorbike theft follows behind, especially in Amsterdam and Brussels. Don't park anywhere a bicycle could be lifted from. Antwerp's port area is a known transit route for stolen bikes heading east — keep the bike in a hotel garage if you're staying in any of the big cities.",
            },
            {
                flag: "🇬🇷",
                name: "Greece",
                body:
                    "Athens has high scooter and small-bike theft, but big tourers are much less commonly targeted. The islands and rural mainland are essentially safe. The slow police response means reporting a theft takes a full afternoon — get a crime reference number for your insurer and budget the time. Carry your IDP; Greek police occasionally insist on it.",
            },
            {
                flag: "🇨🇿🇵🇱🇭🇺",
                name: "Eastern Europe transit risk",
                body:
                    "Bikes stolen in Western Europe are often moved east through Poland, Czechia and Hungary toward Belarus and Ukraine within 24 hours. The implication for riders is mostly indirect — if you're touring east, theft rates are lower in the major cities than the headlines suggest, but recovery is much harder once a bike is over the border into a non-EU country. Trackers with European-wide GSM coverage matter more here than anywhere else.",
            },
        ],
    },
    {
        heading: "Locks — what 'Sold Secure' actually means",
        intro:
            "Sold Secure is a UK rating run by the Master Locksmiths Association. The rating system tells you how long the lock survives attack with specific tools. Most insurers' lock requirements are quoted in Sold Secure ratings, not the brand.",
        items: [
            {
                title: "Bronze, Silver, Gold, Diamond",
                body:
                    "Bronze and Silver are bike-shaped warning signs to opportunist scrotes; not appropriate for anything you'd be heartbroken to lose. Gold is the practical minimum for any decent bike. Diamond is the top rating and what insurers require above ~£10k bike value.",
            },
            {
                title: "Chain thickness matters",
                body:
                    "16mm hardened steel link is the practical sweet spot — enough to slow a battery grinder, light enough to carry. 19mm or 22mm is heavier but takes meaningfully longer to cut. Anything below 14mm is barely a speed bump.",
            },
            {
                title: "D-locks — short and grinder-resistant",
                body:
                    "A short D-lock leaves no chain to grab and reach with a grinder, which is exactly what the latest battery angle grinders are good at. The best modern D-locks carry a Sold Secure Diamond (some Diamond Plus) rating and are independently tested to survive minutes of grinder attack while staying light enough to actually carry — look for that rating rather than a name. Classic hardened D-bars are still solid for everyday use. Downside of any D-lock: shorter reach than a chain — pair one with a chain at home where length matters.",
            },
            {
                title: "What to look for",
                body:
                    "Chains: 16mm hardened steel, Sold Secure Gold or above. D-locks: a short, grinder-resistant body with a Sold Secure Diamond rating. Buy from a UK dealer or directly from the manufacturer to make sure it's a genuine Sold Secure piece — there are knock-offs on Amazon.",
            },
            {
                title: "Skip the budget kit",
                body:
                    "If a chain costs less than £80 it's not protecting a £10k bike. If a disc lock costs less than £30 it's a decoration. Spend the money once and rotate it across bikes.",
            },
        ],
    },
    {
        heading: "Trackers and electronics",
        intro:
            "Locks slow theft down. Trackers help you recover the bike if locks don't. The two are complementary — neither replaces the other.",
        items: [
            {
                title: "Thatcham-approved trackers",
                body:
                    "Insurance often requires a Thatcham Cat 5 or Cat 6 unit for high-value bikes — check before fitting, the wrong category won't qualify for premium reductions.",
            },
            {
                title: "Subscription cost",
                body:
                    "Most decent trackers are subscription-based, £80–£180 a year, for the SIM and the recovery team. Battery-powered units are the notable exception — no subscription, just a one-off device cost.",
            },
            {
                title: "Multi-tech beats GPS-only",
                body:
                    "Cheap GPS-only trackers can be jammed with a £20 device from eBay. Decent trackers combine GPS + GSM + RF + accelerometer — much harder to defeat in one go.",
            },
            {
                title: "Alarms and immobilisers",
                body:
                    "Most modern bikes have factory immobilisers — the key/fob has to be present for the ECU to allow start. Aftermarket alarms add a noise deterrent. Decent ones include a pager that alerts your phone when the bike is touched.",
            },
            {
                title: "Hide the tracker, well",
                body:
                    "If a thief knows the bike has a tracker they'll find it. Common fitting spots (under the tank, behind side panels) are checked first. Have it fitted by a specialist who'll route it somewhere genuinely awkward.",
            },
        ],
    },
    {
        heading: "Insurance — what to know before you claim",
        intro:
            "Most claims fail or pay out less than the owner expects because of policy small print they didn't read. Get this right when you renew, not after the bike is gone.",
        items: [
            {
                title: "Specified lock requirements",
                body:
                    "Many bike policies require a specific lock standard (often Sold Secure Gold or higher) to be in use at the time of theft. If your bike was stolen from outside a café with no chain on it, the insurer may decline. Photograph the lock in use occasionally as proof.",
            },
            {
                title: "Overnight parking address",
                body:
                    "Insurance is rated on where the bike sleeps. Lying about garage vs driveway invalidates the policy. Get the postcode and overnight location right.",
            },
            {
                title: "Agreed value vs market value",
                body:
                    "Standard policies pay market value at time of theft — often much less than you paid. Specialist insurers offer agreed-value policies on classics and modified bikes. Worth the small premium uplift if the bike is rare or modified.",
            },
            {
                title: "Modifications and accessories",
                body:
                    "Aftermarket exhaust, screen, panniers, sat nav mount — declare them. Undeclared mods can void the policy entirely.",
            },
            {
                title: "Photograph everything annually",
                body:
                    "Every angle of the bike, VIN, key serials, receipts for accessories, service history. Email the lot to yourself so it lives in cloud storage. If the bike is taken you can hand it all to the insurer the same day instead of arguing for weeks.",
            },
        ],
    },
    {
        heading: "If it does get stolen",
        items: [
            {
                title: "First hour matters most",
                body:
                    "Call 999 — yes, it's an emergency while the bike is still moving. Provide registration, VIN, colour, last known location, and any tracker provider name. Most recoveries happen within the first six hours.",
            },
            {
                title: "Tracker provider",
                body:
                    "Call their hotline immediately. They'll coordinate with police via a direct line that bypasses the 101 queue. Have your account number ready (save it in your phone now).",
            },
            {
                title: "Insurance — call within 24 hours",
                body:
                    "Some policies require notification within 24 or 48 hours. Get a crime reference number from the police first, then call the insurer. Late notification is a common reason for claim disputes.",
            },
            {
                title: "Social media and the bike community",
                body:
                    "Post on Facebook bike groups, Reddit r/MotoUK, dedicated stolen-bike registries, and your tracker provider's stolen feed. Many stolen bikes are spotted by riders who recognise an unusual modification or sticker.",
            },
            {
                title: "Watch the marketplaces",
                body:
                    "Stolen bikes often appear on eBay and Facebook Marketplace as parts within 48 hours. Search your make + model in your region for unusual listings. If you spot a suspect listing, get a screenshot and pass it to the police — don't confront the seller yourself.",
            },
        ],
    },
];

function BikeSecurityGuide() {
    usePageMeta({
        title: "Keeping your motorbike from being stolen — Planamoto",
        description:
            "A practical guide to motorbike security: how thefts actually happen, what works at home, what to carry on tour, locks, trackers, insurance and what to do if your bike is taken.",
        url: "https://www.planamoto.com/guides/bike-security",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="Keeping your motorbike from being stolen — at home and on tour"
            lede="About 25,000 motorbikes are stolen in the UK each year and the recovery rate sits around 40%. The good news: thieves are mostly opportunists, and a moderately-defended bike is enough work that they'll pick someone else's instead. This guide covers the practical kit and habits that actually move the needle."
            heroImage={{
                src: META.image?.src ?? "/assets/mc_lock.jpg",
                alt:
                    META.image?.alt ??
                    "A LITELOK D-lock secured through the spokes and brake disc of a parked motorbike's rear wheel",
            }}
            beforeSections={
                <>
                    <GuideOpinionLead {...OPINION} />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The general playbook
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            That's what works for me. Here's the full picture.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Knowing how bikes actually get taken tells you what to
                            defend against. Work through it in order — home first,
                            then the road, then the kit that backs it all up.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            {SECTIONS.map((s, i) => (
                <GuideSection
                    key={s.heading}
                    index={i + 1}
                    heading={s.heading}
                    intro={s.intro}
                    items={s.items}
                    countries={s.countries}
                    photoPlaceholder={s.photoPlaceholder}
                />
            ))}
            <RelatedGuides
                slugs={["packing-europe", "french-toll-roads", "fuel-prices"]}
            />
            <CtaCard
                title="More guides"
                body="Heading abroad? Read our packing checklist for France, Germany, Switzerland and Italy, or the French toll roads primer."
                primary={{ label: "Browse all guides", to: "/guides" }}
            />
        </GuideArticle>
    );
}

export default BikeSecurityGuide;
